import { z } from 'zod';
import { BuildJobStatusSchema, BuildTypeSchema } from './types.schema.js';

/**
 * Start build job request schema
 */
export const StartBuildJobRequestSchema = z.object({
  buildType: BuildTypeSchema.describe('Type of build to create'),
  revision: z.string().optional().describe('Code revision to build (defaults to "latest")'),
});

/**
 * Start build job response schema
 */
export const StartBuildJobResponseSchema = z.object({
  success: z.boolean().describe('Indicates if the build job was started successfully'),
  buildId: z.string().describe('Unique identifier for the build job'),
  message: z.string().describe('Success message'),
  jobName: z.string().describe('Name/ID of the Cloud Run job execution'),
  timestamp: z.string().describe('ISO timestamp when the job was started'),
});

/**
 * Build job info schema (used in lists and individual responses)
 */
export const BuildJobInfoSchema = z.object({
  buildId: z.string().describe('Unique identifier for the build job'),
  projectId: z.string().describe('Project ID the build belongs to'),
  userId: z.string().describe('User ID the build belongs to'),
  buildType: BuildTypeSchema.describe('Type of build'),
  status: BuildJobStatusSchema.describe('Current status of the build job'),
  executionId: z.string().describe('Cloud Run execution ID'),
  creationTimestamp: z.string().describe('ISO timestamp when the build was created'),
  lastUpdateTimestamp: z.string().describe('ISO timestamp when the build was last updated'),
  params: z.record(z.string(), z.string()).describe('Build parameters'),
  errorMessage: z.string().optional().describe('Error message if the build failed'),
});

/**
 * Build job info schema (used in lists and individual responses)
 */
export const BuildJobListInfoSchema = z.object({
  buildId: z.string().describe('Unique identifier for the build job'),
  buildType: BuildTypeSchema.describe('Type of build'),
  status: BuildJobStatusSchema.describe('Current status of the build job'),
  creationTimestamp: z.string().describe('ISO timestamp when the build was created'),
  lastUpdateTimestamp: z.string().describe('ISO timestamp when the build was last updated'),
});

/**
 * Sort field options for build jobs
 */
export const BuildJobSortBySchema = z.enum(['creation', 'update']).describe('Field to sort by');

/**
 * Sort order options
 */
export const SortOrderSchema = z.enum(['asc', 'desc']).describe('Sort order');

/**
 * List build jobs request schema (query parameters)
 */
export const ListBuildJobsRequestSchema = z.object({
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(100)
    .optional()
    .describe('Maximum number of builds to return (default: 20, max: 100)'),
  startAfter: z.string().optional().describe('Document ID to start after (for cursor pagination)'),
  buildTypes: z.array(BuildTypeSchema).optional().describe('Filter by build types'),
  statuses: z.array(BuildJobStatusSchema).optional().describe('Filter by statuses'),
  sortBy: BuildJobSortBySchema.optional().describe('Field to sort by (default: creation)'),
  sortOrder: SortOrderSchema.optional().describe('Sort order (default: desc)'),
});

/**
 * Pagination info schema
 */
export const PaginationInfoSchema = z.object({
  limit: z.number().int().describe('Number of items requested'),
  hasMore: z.boolean().describe('Whether there are more items available'),
  nextCursor: z.string().optional().describe('Document ID for next page'),
});

/**
 * List build jobs response schema
 */
export const ListBuildJobsResponseSchema = z.object({
  success: z.boolean().describe('Indicates if the request was successful'),
  builds: z.array(BuildJobListInfoSchema).describe('Array of build job information'),
  pagination: PaginationInfoSchema.describe('Pagination information'),
  timestamp: z.string().describe('ISO timestamp of the response'),
});

/**
 * Count build jobs request schema (query parameters)
 */
export const CountBuildJobsRequestSchema = z.object({
  buildTypes: z.array(BuildTypeSchema).optional().describe('Filter by build types'),
  statuses: z.array(BuildJobStatusSchema).optional().describe('Filter by statuses'),
});

/**
 * Count build jobs response schema
 */
export const CountBuildJobsResponseSchema = z.object({
  success: z.boolean().describe('Indicates if the request was successful'),
  count: z.number().int().min(0).describe('Total number of build jobs matching the criteria'),
  filters: z
    .object({
      buildTypes: z.array(BuildTypeSchema).optional(),
      status: z.string().optional(),
    })
    .optional()
    .describe('Applied filters'),
  timestamp: z.string().describe('ISO timestamp of the response'),
});

/**
 * Get build job response schema
 */
export const GetBuildJobResponseSchema = z.object({
  success: z.boolean().describe('Indicates if the request was successful'),
  buildJob: BuildJobInfoSchema.describe('Build job information'),
  timestamp: z.string().describe('ISO timestamp of the response'),
});
/**
 * Get build job URL response schema
 */
export const GetBuildUrlResponseSchema = z.object({
  success: z.literal(true).describe('Indicates the request was successful'),
  buildUrl: z.string().url().describe('URL to access the build artifacts'),
  buildType: BuildTypeSchema.describe('Type of build'),
  timestamp: z.string().describe('ISO timestamp of the response'),
});

// Note: UpdateBuildStatusRequest is defined in @darvin/build-service-common
// as it's for internal API communication between build job and service

// Export TypeScript types
export type StartBuildJobRequest = z.infer<typeof StartBuildJobRequestSchema>;
export type StartBuildJobResponse = z.infer<typeof StartBuildJobResponseSchema>;
export type BuildJobInfo = z.infer<typeof BuildJobInfoSchema>;
export type BuildJobListInfo = z.infer<typeof BuildJobListInfoSchema>;
export type ListBuildJobsRequest = z.infer<typeof ListBuildJobsRequestSchema>;
export type ListBuildJobsResponse = z.infer<typeof ListBuildJobsResponseSchema>;
export type CountBuildJobsRequest = z.infer<typeof CountBuildJobsRequestSchema>;
export type CountBuildJobsResponse = z.infer<typeof CountBuildJobsResponseSchema>;
export type GetBuildJobResponse = z.infer<typeof GetBuildJobResponseSchema>;
// UpdateBuildStatusRequest type is exported from @darvin/build-service-common
export type PaginationInfo = z.infer<typeof PaginationInfoSchema>;
export type GetBuildUrlResponse = z.infer<typeof GetBuildUrlResponseSchema>;
